// Shared CSV export — build once; every table in WigleyHub reuses this.
//   exportToCsv(filename, columns, rows, options)
//     columns: [{ key, label, type }]  — header from labels, each cell from row[col.key];
//                                        `type` (optional) drives output rounding, see below
//     rows:    array of objects keyed by column.key (null/undefined -> '')
//     options: { estimatedColumns } — optional; see below. Omit it and behaviour is unchanged.
// Escaping (RFC-4180): a field containing a comma, double-quote, CR or LF is wrapped
// in double-quotes, and embedded double-quotes are doubled. A UTF-8 BOM is prepended
// so Excel detects the encoding (accents/€/etc. render correctly). Download is triggered
// client-side via a Blob + a temporary <a download>.
//
// ESTIMATES. A cell value may be { value, estimated } — the report layer's shape for "this
// figure is an estimate". CSV has no number formats, so unlike the XLSX export there is no way
// to show the marker without touching the value. The two options are mutually exclusive and the
// `estimatedColumns` flag picks between them:
//   OFF (default) — the asterisk is APPENDED to the value: 8901.24 -> "8901.24*". Matches what
//     is on screen, which is the export contract, at the cost of making that cell text in Excel.
//   ON            — the value stays clean and the flag moves to its own TRUE/FALSE column beside
//     it. This is the point of the option: a number format can't be pivoted or filtered, and
//     neither can "8901.24*". An analyst who needs to sum or filter turns this on and gets both
//     a summable number and a filterable flag.
// Never both: an asterisk AND a companion column would double-mark the same fact and re-break
// the numeric cell the option exists to protect.

// ⛔ ROUND AT OUTPUT. CSV carries the raw value with no format to hide it, so a summed
// 719254.0399999999 ships to the client exactly as stored. Money to 2dp, a rate to 6dp in percent
// units. Rounding happens HERE, at the boundary — never in
// the derivation, which keeps full precision so totals still reconcile.
const OUTPUT_DP = { money: 2, value: 2, rate: 6, percent: 2, int: 0, year: 0 };
const NUMERIC_STRING = /^-?(\d+(\.\d*)?|\.\d+)$/;
function roundForCsv(value, type) {
  const dp = OUTPUT_DP[type];
  if (dp == null) return value;
  const n = typeof value === 'number' ? value
    : (NUMERIC_STRING.test(String(value ?? '').replace(/[$%\s,]/g, '')) ? parseFloat(String(value).replace(/[$%\s,]/g, '')) : null);
  if (n == null || !Number.isFinite(n)) return value;      // a label in a numeric column stays a label
  return Math.round((n + Number.EPSILON) * 10 ** dp) / 10 ** dp;
}

function escapeCell(value) {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// { value, estimated } -> its parts; anything else passes through unflagged.
function unwrap(raw) {
  if (raw && typeof raw === 'object' && !(raw instanceof Date) && 'value' in raw) {
    return { value: raw.value, estimated: !!raw.estimated, recovered: !!raw.recovered, effective: !!raw.effective };
  }
  return { value: raw, estimated: false, recovered: false, effective: false };
}

// Which columns carry an estimate flag anywhere in this data set — only those get a companion.
function estimateCarriers(columns, rows) {
  const carriers = new Set();
  (rows || []).forEach((row) => (columns || []).forEach((col) => {
    if (unwrap(row[col.key]).estimated) carriers.add(col.key);
  }));
  return carriers;
}

export function exportToCsv(filename, columns, rows, options = {}) {
  const cols = (columns || []).filter(Boolean);
  const withFlags = !!options.estimatedColumns;
  const carriers = withFlags ? estimateCarriers(cols, rows) : new Set();

  // Companion columns are interleaved right after the column they shadow, matching the XLSX
  // export so the two files line up column-for-column.
  const outCols = [];
  cols.forEach((col) => {
    outCols.push({ ...col, _src: col.key });
    if (carriers.has(col.key)) outCols.push({ key: `__estimated__${col.key}`, label: `${col.label ?? col.key} Estimated`, _flagFor: col.key });
  });

  const cellText = (row, col) => {
    if (col._flagFor) return unwrap(row[col._flagFor]).estimated ? 'TRUE' : 'FALSE';
    const { value, estimated, recovered, effective } = unwrap(row[col._src]);
    if (value === null || value === undefined) return '';
    const out = roundForCsv(value, col.type);
    // Asterisk only when the flag is NOT being carried in its own column (see the note above).
    // ⛔ Both marks, and they are not interchangeable: `*` estimated (rate from another year),
    // `†` recovered (rate derived from the bill's own arithmetic), `‡` effective (the bill's ad valorem ÷ taxable).
    // See RECOVERED_FOOTNOTE and EFFECTIVE_FOOTNOTE.
    // ⛔ Only the ESTIMATE flag has a companion column; `recovered` does not, so it stays inline
    // even under withFlags. Dropping it there would lose the caveat in exactly the mode an analyst
    // turns on to be more rigorous.
    const marks = `${estimated && !withFlags ? '*' : ''}${recovered ? '†' : ''}${effective ? '‡' : ''}`;
    return marks ? `${out}${marks}` : out;
  };

  const lines = [outCols.map((col) => escapeCell(col.label)).join(',')];
  for (const row of (rows || [])) {
    lines.push(outCols.map((col) => escapeCell(cellText(row, col))).join(','));
  }
  const csv = '﻿' + lines.join('\r\n') + '\r\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
